import { Debounce } from "@/components/Debounce";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Check, Loader2, XIcon } from "lucide-react";
import {
  ChangeEventHandler,
  HTMLAttributes,
  MutableRefObject,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import type { Option } from "../AsyncSelect";

export interface CommandTreeProps {
  enableFilter?: boolean;
  filterInputValue: string;
  onValueChange: ChangeEventHandler<HTMLInputElement>;
  onClear: () => void;
  searchLabel?: string;
  isFetching: boolean;
  loadingLabel?: string;
  noResultsLabel?: string;
  data: Option[] | undefined;
  selectedId: MutableRefObject<unknown>;
  renderName?: (item: Option) => ReactNode;
  onSelect: (id: string) => void;
  allowClearSelection?: boolean;
  clearLabel?: string;
  onClearSelection: () => void;
  inputProps?: HTMLAttributes<HTMLInputElement>;
}

const CommandTree = ({
  enableFilter,
  filterInputValue,
  onValueChange,
  onClear,
  searchLabel,
  isFetching,
  loadingLabel,
  noResultsLabel,
  data,
  selectedId,
  renderName,
  onSelect,
  allowClearSelection,
  clearLabel,
  onClearSelection,
  inputProps,
}: CommandTreeProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <Command shouldFilter={false} className={cn(isMobile && "h-full")}>
      {enableFilter && (
        <Debounce onDebouncedChange={onValueChange}>
          <CommandInput
            value={filterInputValue}
            placeholder={searchLabel}
            isFetching={isFetching}
            onClear={onClear}
            {...inputProps}
          />
        </Debounce>
      )}
      <CommandList className={cn(isMobile && "max-h-none flex-1")}>
        <CommandEmpty>
          {isFetching ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingLabel && (
                <span className="text-slate-500">{loadingLabel}</span>
              )}
            </span>
          ) : (
            noResultsLabel ?? t("common.noResults")
          )}
        </CommandEmpty>
        <CommandGroup>
          {data?.map((item) => (
            <CommandItem key={item.id} value={item.id} onSelect={onSelect}>
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  selectedId.current === item.id ? "opacity-100" : "opacity-0",
                )}
              />
              {renderName ? renderName(item) : item.name}
            </CommandItem>
          ))}
        </CommandGroup>
        {allowClearSelection && (
          <CommandGroup className="sticky bottom-0 border-t bg-popover">
            <CommandItem value="" onSelect={onClearSelection}>
              <XIcon className="mr-2 h-4 w-4" />
              {clearLabel || t("asyncSelect.clear")}
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};

export default CommandTree;
